import { Injectable } from '@angular/core';
import { EngineRegistryService } from './engine-registry.service';
import { ProcessingImage, PipelineStep } from '../../main/models/processing-pipeline';
import { TOOL_BY_OPERATION_ID } from '../../main/models/jit-tool-registry.helpers';

export interface StepResult {
  stepIndex: number;
  image: ProcessingImage;
}

/**
 * Executes a pipeline up to a given step index, caching intermediate results.
 */
@Injectable({ providedIn: 'root' })
export class PipelineExecutorService {
  /** Cached intermediate results keyed by step index. */
  private cache: Map<number, ProcessingImage> = new Map();
  /** The steps that produced the cached results (for invalidation). */
  private cachedSteps: PipelineStep[] = [];
  /** Engine currently executing (set during executeUpTo, cleared on completion). */
  private _activeEngine: any = null;

  constructor(private registry: EngineRegistryService) {}

  /**
   * Execute the pipeline from the input image up to (and including) the given step index.
   * Uses cached intermediates when possible.
   */
  async executeUpTo(
    inputImage: ProcessingImage,
    steps: PipelineStep[],
    upToIndex: number
  ): Promise<ProcessingImage> {
    // Find the latest valid cached result
    let startIndex = 0;
    let current = inputImage;

    for (let i = 0; i <= upToIndex; i++) {
      if (this.isCacheValid(i, steps)) {
        current = this.cache.get(i)!;
        startIndex = i + 1;
      } else {
        break;
      }
    }

    // Execute from startIndex to upToIndex
    for (let i = startIndex; i <= upToIndex; i++) {
      const step = steps[i];
      if (!step.enabled) {
        this.cache.set(i, current);
        this.cachedSteps[i] = { ...step };
        continue;
      }

      // Server-engine steps usually run only when "Run on Server" submits a
      // CompoundRequest, so during local preview we pass through. Exception:
      // when the JIT tool declares a `previewEngineId`, the tool has a browser
      // implementation registered under that engine id (today: cellpose-sam ->
      // cellpose-js / WebGPU). Delegate `execute()` to that engine instead.
      if (step.engineId === 'server') {
        const tool = TOOL_BY_OPERATION_ID.get(step.operationId);
        // `previewEngineId` says a browser implementation *exists*, not that this
        // build contributed it. The three JAX engines (yolo-seg, retinal-layer,
        // stain-adapter) are absent from any build without
        // @jds/jit-extensions, while their operations remain in the list
        // unconditionally -- they come from JIT_TOOLS, and the plugin's engines
        // declare no operations of their own. So probe the registry first and
        // fall through to server pass-through when nothing claims the id, rather
        // than throwing `Engine "..." not registered` at the user. These are real
        // server tools; the server has the models. Only the local preview is
        // missing, which is the same position as every other server-only step.
        if (tool?.previewEngineId && this.registry.getEngine(tool.previewEngineId)) {
          const previewEngine = await this.registry.ensureEngineReady(tool.previewEngineId);
          // The server engine's OperationDescriptor (from getAllServerOperations())
          // is suitable here — the engines that act as preview providers ignore
          // the `operation` arg and read everything from `params`.
          const serverEngine = await this.registry.ensureEngineReady('server');
          const op = serverEngine.operations.find((o) => o.id === step.operationId);
          if (!op) {
            throw new Error(`No registry entry for operation: ${step.operationId}`);
          }
          this._activeEngine = previewEngine;
          try {
            current = await previewEngine.execute(op, current, step.params);
          } finally {
            this._activeEngine = null;
          }
          this.cache.set(i, current);
          this.cachedSteps[i] = { ...step };
          continue;
        }
        // Default: server-engine pass-through.
        this.cache.set(i, current);
        this.cachedSteps[i] = { ...step };
        continue;
      }

      const engine = await this.registry.ensureEngineReady(step.engineId);
      const operation = engine.operations.find((op) => op.id === step.operationId);
      if (!operation) {
        throw new Error(`Operation "${step.operationId}" not found in engine "${step.engineId}"`);
      }

      this._activeEngine = engine;
      try {
        current = await engine.execute(operation, current, step.params);
      } finally {
        this._activeEngine = null;
      }
      this.cache.set(i, current);
      this.cachedSteps[i] = { ...step };
    }

    return current;
  }

  /** Abort any in-progress long-running operation on the active engine. */
  cancelCurrentOperation(): void {
    this._activeEngine?.cancelCurrentOperation?.();
  }

  /** Clear all cached results (e.g. when input image changes). */
  clearCache(): void {
    this.cache.clear();
    this.cachedSteps = [];
  }

  /**
   * Invalidate cache from a given step index onward.
   * Call when a step is modified, reordered, or removed.
   */
  invalidateFrom(stepIndex: number): void {
    for (const key of Array.from(this.cache.keys())) {
      if (key >= stepIndex) {
        this.cache.delete(key);
      }
    }
    this.cachedSteps.splice(stepIndex);
  }

  private isCacheValid(index: number, steps: PipelineStep[]): boolean {
    if (!this.cache.has(index)) return false;
    const cached = this.cachedSteps[index];
    if (!cached) return false;
    const current = steps[index];
    return (
      cached.operationId === current.operationId &&
      cached.engineId === current.engineId &&
      cached.enabled === current.enabled &&
      JSON.stringify(cached.params) === JSON.stringify(current.params)
    );
  }
}
